import { createTheme } from '@mui/material/styles'

// 彩票應用統一主題配置
export const lotteryTheme = createTheme({
  palette: {
    primary: {
      main: '#ff6b6b', // 主要紅色
      light: '#ff8f8f',
      dark: '#ee5a24',
    },
    secondary: {
      main: '#ffd700', // 金色
      light: '#ffed4a',
      dark: '#ff8f00',
    },
    success: {
      main: '#00b894', // 綠色
      light: '#00d4aa',
      dark: '#00a085',
    },
    info: {
      main: '#74b9ff', // 藍色
      light: '#a7d4ff',
      dark: '#0984e3',
    },
    warning: {
      main: '#ffeaa7', // 警告橙色
      light: '#fdcb6e',
      dark: '#e17055',
    },
    error: {
      main: '#ff7675', // 錯誤紅色
      light: '#ff9ff3',
      dark: '#d63031',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: [
      'Microsoft YaHei',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 900,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 800,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0, 0, 0, 0.05)',
    '0 4px 15px rgba(0, 0, 0, 0.1)',
    '0 8px 25px rgba(0, 0, 0, 0.15)',
    '0 20px 40px rgba(0, 0, 0, 0.1)',
    '0 8px 25px rgba(255, 107, 107, 0.3)',
    '0 8px 25px rgba(255, 215, 0, 0.3)',
    '0 8px 25px rgba(0, 184, 148, 0.3)',
    '0 4px 15px rgba(0, 0, 0, 0.1)',
    '0 5px 15px rgba(0, 0, 0, 0.08)',
    '0 6px 12px rgba(0, 0, 0, 0.1)',
    '0 8px 20px rgba(0, 0, 0, 0.12)',
    '0 10px 25px rgba(0, 0, 0, 0.15)',
    '0 12px 30px rgba(0, 0, 0, 0.18)',
    '0 15px 35px rgba(0, 0, 0, 0.2)',
    '0 18px 40px rgba(0, 0, 0, 0.22)',
    '0 20px 45px rgba(0, 0, 0, 0.25)',
    '0 25px 50px rgba(0, 0, 0, 0.3)',
    '0 30px 60px rgba(0, 0, 0, 0.35)',
    '0 35px 70px rgba(0, 0, 0, 0.4)',
    '0 40px 80px rgba(0, 0, 0, 0.45)',
    '0 45px 90px rgba(0, 0, 0, 0.5)',
    '0 50px 100px rgba(0, 0, 0, 0.55)',
    '0 60px 120px rgba(0, 0, 0, 0.6)',
    '0 70px 140px rgba(0, 0, 0, 0.65)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #ff8f8f 0%, #ff6b6b 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #ffd700 0%, #ff8f00 100%)',
          color: '#333',
          '&:hover': {
            background: 'linear-gradient(135deg, #ffed4a 0%, #ffd700 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff6b6b',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff6b6b',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 600,
        },
        colorPrimary: {
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
        },
        colorSecondary: {
          background: 'linear-gradient(135deg, #ffd700 0%, #ff8f00 100%)',
          color: '#333',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          borderRadius: '16px 16px 0 0',
        },
      },
    },
  },
})

// CSS-in-JS 樣式常量
export const lotteryStyles = {
  // 漸變背景
  gradients: {
    primary: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    secondary: 'linear-gradient(135deg, #ffd700 0%, #ff8f00 100%)',
    purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
    royal: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
    pink: 'linear-gradient(135deg, #fd79a8, #e84393)',
  },
  
  // 陰影效果
  shadows: {
    light: '0 4px 15px rgba(0, 0, 0, 0.1)',
    medium: '0 8px 25px rgba(0, 0, 0, 0.15)',
    heavy: '0 20px 40px rgba(0, 0, 0, 0.1)',
    colored: '0 8px 25px rgba(255, 107, 107, 0.3)',
    wealth: '0 8px 25px rgba(255, 215, 0, 0.3)',
    success: '0 8px 25px rgba(0, 184, 148, 0.3)',
  },
  
  // 動畫效果
  animations: {
    float: `
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-10px) rotate(5deg); }
      }
    `,
    shimmer: `
      @keyframes shimmer {
        0%, 100% { transform: rotate(0deg); }
        50% { transform: rotate(180deg); }
      }
    `,
    diceRoll: `
      @keyframes diceRoll {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `,
    twinkle: `
      @keyframes twinkle {
        0%, 100% { opacity: 0.3; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.2); }
      }
    `,
    slideShine: `
      @keyframes slideShine {
        0% { left: -100%; }
        100% { left: 100%; }
      }
    `,
  },
  
  // 玻璃質感效果
  glass: {
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },
  
  // 卡片樣式
  luxuryCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 20,
    padding: 30,
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    },
  },
}

export default lotteryTheme